import React, {useEffect, useState} from 'react';
import {Cell, Label, Legend, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';
import {motion} from 'framer-motion';
import {RootState, useAppSelector} from '../../../../../state/state';
import {OrderUi} from '../../../../Subscriptions/types/OrderListUi';

export default function StatsForOrders() {

    const orders = useAppSelector((state: RootState) => state.OrderStatsSlice.data);

    const [stats, setStats] = useState({
        paid: 0,
        unpaid: 0,
        settled: 0,
    });
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    const {subscriptionId, cycleName} = useAppSelector((state:RootState) => state.ArbitrarySlice.ForStatsForOrders);

    const calculateStats = () => {
        if (!orders || orders.length === 0) {
            setStats({ paid: 0, unpaid: 0, settled: 0 });
            setLoading(false);
            return;
        }

        let filteredOrders = orders;

        // Filter by subscriptionId if provided
        if (subscriptionId) {
            filteredOrders = filteredOrders.filter(order => order.subscriptionId === subscriptionId);
        }

        // Filter by cycleName if provided and not "All Cycles"
        if (cycleName && cycleName !== "All Cycles") {
            filteredOrders = filteredOrders.filter(order => order.cycleName === cycleName);
        }

        const paidOrders = filteredOrders.filter(order => order.paymentstatus === 'Paid');
        const unpaidOrders = filteredOrders.filter(order => order.paymentstatus !== 'Paid');
        const settledOrders = filteredOrders.filter(order => order.settlementStatus === 'Settled');

        const totalPaidCharges = paidOrders.reduce((sum, order) => sum + (order.chargeable || 0), 0);
        const totalUnpaidCharges = unpaidOrders.reduce((sum, order) => sum + (order.chargeable || 0), 0);
        const settledAmount = settledOrders.reduce((sum, order) => sum + (order.chargeable || 0), 0);

        setStats({
            paid: Number(totalPaidCharges.toFixed(2)),
            unpaid: Number(totalUnpaidCharges.toFixed(2)),
            settled: Number(settledAmount.toFixed(2)),
        });
        setLoading(false);
    };

    useEffect(() => {
        calculateStats();
    }, [orders, subscriptionId, cycleName]);

    const COLORS = ['#0353a4', '#ff5400', '#31cb00'];

    const data = [
        {name: 'Payment Received', value: stats.paid, color: COLORS[0]},
        {name: 'Upcoming Payment', value: stats.unpaid, color: COLORS[1]},
        {name: 'Settled', value: stats.settled, color: COLORS[2]},
    ];

    const openModal = () => setModalOpen(true);
    const closeModal = () => setModalOpen(false);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    if(stats.paid===0 && stats.unpaid===0 && stats.settled===0) {
        return (
            <div className="flex flex-col bg-white border min-h-56 shadow-sm rounded-xl p-3 md:p-3">
                <div className="text-center mt-4">
                    <h3 className="text-lg sm:text-xl font-medium text-gray-800">Payment Overview</h3>
                </div>
                <div className={"h-48 w-full flex justify-center items-center"}>
                    <h3>Please Raise orders to see insights on it.</h3>
                </div>
            </div>
        )
    }

    return (
        <div className="mt-4 ">
            <div className="   max-w-[85rem]  mx-auto ">
                {loading ? (
                    <div className="flex justify-center items-center">
                        <p>Loading...</p>
                    </div>
                ) : (
                    <div className="flex border-[1px] border-gray-300 flex-col bg-white border shadow-sm rounded-xl p-3 md:p-3">
                        <div className="text-center mt-4">
                            <h3 className="text-lg sm:text-xl font-medium text-gray-800">Payment Overview</h3>
                        </div>
                        <motion.div
                            initial={{opacity: 0, y: 20}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.5}}
                            onClick={openModal}
                            className="cursor-pointer"
                        >
                            <ResponsiveContainer width="100%" height={400}>
                                <PieChart>
                                    <Pie
                                        data={data}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={130}
                                        innerRadius={80}  // This creates the donut effect
                                        fill="#8884d8"
                                    >
                                        {data.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color}/>
                                        ))}
                                        <Label
                                            value={`Total: ${(stats.paid + stats.unpaid).toFixed(2)}`}
                                            position="center"
                                            className="text-md font-medium"
                                            fill="#000"
                                        />
                                    </Pie>
                                    <Tooltip/>
                                    <Legend verticalAlign="bottom"/>
                                </PieChart>
                            </ResponsiveContainer>
                        </motion.div>
                        {modalOpen && (
                            <div
                                className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
                                onClick={handleBackdropClick}
                            >
                                <div className="relative bg-white rounded-lg w-3/4 sm:w-1/2 p-4"
                                     style={{height: '90%', width: "60%"}}>
                                    <button
                                        onClick={closeModal}
                                        className="absolute top-2 right-2 text-4xl font-bold text-gray-800 bg-gray-100 rounded-full w-14 h-14 flex items-center justify-center"
                                        aria-label="Close modal"
                                    >
                                        ×
                                    </button>
                                    <div className="text-center mb-4">
                                        <h3 className="text-xl font-semibold text-gray-800">Payment Stats</h3>
                                    </div>
                                    <div className="w-full h-[calc(100%-4rem)]"> {/* Adjust height to fit modal */}
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={data}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={200}
                                                    innerRadius={120}  // This creates the donut effect
                                                    fill="#8884d8"
                                                    label={({name, value}) => `${name}: ${value}`}
                                                >
                                                    {data.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color}/>
                                                    ))}
                                                    <Label
                                                        value={`Total: ${(stats.paid + stats.unpaid).toFixed(2)}`}
                                                        position="center"
                                                        className="text-lg font-medium"
                                                        fill="#000"
                                                    />
                                                </Pie>
                                                <Tooltip/>
                                                <Legend verticalAlign="bottom"/>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
